import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from "@lexical/markdown";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import {
  InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { SelectionAlwaysOnDisplay } from "@lexical/react/LexicalSelectionAlwaysOnDisplay";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { cn } from "../../utils/tailwind";
import { theme } from "./Theme";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { SuggestionNode } from "./plugins/Suggestion/SuggestionNode";
import { PasteMarkdownPlugin } from "./plugins/PasteMarkdownPlugin";
import { logger } from "../../common/logger";
import { FormattedMessage, defineMessage, useIntl } from "react-intl";
import {
  PropsWithChildren,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $insertNodes,
  LexicalEditor,
} from "lexical";
import { FaMarkdown } from "react-icons/fa6";
import { TRANSFORMERS } from "./transformer";
import TreeViewPlugin from "./plugins/TreeViewPlugin";
import { debounce } from "lodash";
import {
  EditorMode,
  formatEditorMode,
  getEditorMode,
  setEditorMode,
} from "./utils";
import { Button } from "@/kit/ui/button";

const defaultPlaceholder = defineMessage({ defaultMessage: "Start writing.." });

export const EDITOR_NAMESPACE = "markdown-editor";

const EDITOR_NODES = Object.freeze([
  ListNode,
  ListItemNode,
  HorizontalRuleNode,
  HeadingNode,
  QuoteNode,
  SuggestionNode,
]);

const UNESCAPE = /\\([\\`*_{}[\]()#+\-.!|>~])/g;

export interface WriteProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    PropsWithChildren {
  namespaceId?: ID;
  value?: string;
}

const serializeMarkdown = (editor: LexicalEditor, mode: EditorMode) =>
  mode === "plain"
    ? editor.read(() =>
        $getRoot()
          .getChildren()
          .map((n) => n.getTextContent())
          .join("\n"),
      )
    : editor.read(() =>
        $convertToMarkdownString(TRANSFORMERS)
          .replace(/\n{2}/g, "\n")
          .replace(UNESCAPE, "$1"),
      );

const deserializeMarkdown = (
  editor: LexicalEditor,
  markdown: string,
  mode: EditorMode,
) =>
  editor.update(() => {
    if (mode === "wysiwyg") {
      $convertFromMarkdownString(markdown, TRANSFORMERS, undefined, true, true);
      return;
    }

    const paragraphs = markdown.split(/\n/).map((line) => {
      const p = $createParagraphNode();
      if (line) p.append($createTextNode(line));
      return p;
    });

    editor.update(() => {
      const root = $getRoot();
      root.clear();
      $insertNodes(paragraphs);
    });
  });

// Workaround to ensure we can write within an empty editor (without a default value)
// by ensuring we are not going to autofocus in it.
// Original version: https://github.com/facebook/lexical/issues/4474#issuecomment-1558485693
// The tag system mentioned afterward does not work here.
const ensureEmptyParagraph = (
  editor: LexicalEditor,
  autoFocus?: boolean,
): void => {
  if (!autoFocus) editor.setEditable(false);
  editor.update(() => {
    $getRoot().clear();
    $insertNodes([$createParagraphNode()]);
  });
  if (!autoFocus) requestAnimationFrame(() => editor.setEditable(true));
};

export const Write = forwardRef<LexicalEditor | null, WriteProps>(
  ({ namespaceId, value, onChange, children, ...rest }, ref) => {
    const intl = useIntl();
    const [mode, setMode] = useState<EditorMode>("wysiwyg");
    const prevMode = useRef<EditorMode>(mode);
    const editorRef = useRef<LexicalEditor | null>(null);
    useImperativeHandle(ref, () => editorRef.current, []);

    const namespace = namespaceId
      ? `${EDITOR_NAMESPACE}-${namespaceId}`
      : EDITOR_NAMESPACE;

    // Detect the user’s preference (“plain” or “wysiwyg”) once the component
    // is running in the browser.  During SSR we always fall back to "plain".
    useEffect(() => setMode(getEditorMode), []);

    // Keep the parent form in sync with the editor’s contents:
    //   1. Serialize the Lexical document to Markdown.
    //   2. Debounce updates to avoid flooding React with re‑renders.
    //   3. Unregister the listener and cancel the debounce timer on cleanup.
    useEffect(() => {
      if (!editorRef.current || !onChange) return;

      const debounced = debounce(() => {
        onChange({
          target: { value: serializeMarkdown(editorRef.current!, mode) },
        } as React.ChangeEvent<HTMLInputElement>);
      }, 120);

      const unregister = editorRef.current.registerUpdateListener(debounced);
      return () => {
        unregister();
        debounced.cancel();
      };
    }, [onChange, mode]);

    // When the user flips between “plain” and “wysiwyg” view,
    // re‑parse the current document so the visual output remains identical.

    useEffect(() => {
      if (!editorRef.current || prevMode.current === mode) return;
      const markdown = serializeMarkdown(editorRef.current, prevMode.current);
      prevMode.current = mode;
      deserializeMarkdown(editorRef.current, markdown, mode);
    }, [mode]);

    // If the controlled `value` prop becomes `undefined`,
    // reset the editor to a single empty paragraph
    // (prevents an empty <root> node from ever being committed).
    useEffect(() => {
      if (!editorRef.current) return;
      if (value === undefined) {
        ensureEmptyParagraph(editorRef.current);
      }
    }, [value]);

    const initialConfig = useMemo<InitialConfigType>(
      () => ({
        theme,
        namespace,
        nodes: EDITOR_NODES,
        editorState(editor) {
          editorRef.current = editor;
          if (value) {
            deserializeMarkdown(editor, value, mode);
          } else {
            ensureEmptyParagraph(editor, rest.autoFocus);
          }
        },
        onError(error) {
          logger.error(error);
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [namespace, mode, rest.autoFocus],
    );

    return (
      <div className="relative">
        <LexicalComposer initialConfig={initialConfig}>
          <EditorCore mode={mode} {...rest} />
          {children}
        </LexicalComposer>
        <div className="flex flex-col items-start gap-2">
          {mode === "wysiwyg" && <Footer />}
          <Button
            size="sm"
            variant="link"
            onClick={() => {
              const newMode = mode === "plain" ? "wysiwyg" : "plain";
              setMode(newMode);
              setEditorMode(newMode);
            }}
          >
            {formatEditorMode(intl, mode)}
          </Button>
        </div>
      </div>
    );
  },
);

interface EditorCoreProps extends React.InputHTMLAttributes<HTMLInputElement> {
  mode: EditorMode;
}

const EditorCore = memo(function EditorCore({
  mode,
  className,
  placeholder,
  "aria-placeholder": ariaPlaceholder,
  ...rest
}: EditorCoreProps) {
  const intl = useIntl();
  const contentEditable = (
    <ContentEditable
      className={cn(theme.root, className)}
      placeholder={
        placeholder ? (
          <Placeholder>{placeholder}</Placeholder>
        ) : (
          <DefaultPlaceholder />
        )
      }
      aria-placeholder={
        ariaPlaceholder ?? intl.formatMessage(defaultPlaceholder)
      }
      {...rest}
    />
  );

  return mode === "plain" ? (
    <>
      <PlainTextPlugin
        contentEditable={contentEditable}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <CorePlugin />
    </>
  ) : (
    <>
      <RichTextPlugin
        contentEditable={contentEditable}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
      <TabIndentationPlugin maxIndent={7} />
      <ListPlugin />
      <CorePlugin />
    </>
  );
});

const CorePlugin = () => (
  <>
    <HistoryPlugin />
    <SelectionAlwaysOnDisplay />
    <PasteMarkdownPlugin />
    {import.meta.env.VITE_EDITOR_TREE_VIEW === "true" && <TreeViewPlugin />}
  </>
);

const Footer = memo(() => (
  <>
    <div className="flex gap-2 items-center mt-1">
      <FaMarkdown aria-hidden="true" focusable="false" />
      <p className="prose-none text-sm text-grey-500">
        <FormattedMessage defaultMessage="Support Markdown" />
      </p>
    </div>
    <p className="sr-only">
      <FormattedMessage defaultMessage="A rich text editor supporting Markdown for easy formatting." />
    </p>
  </>
));

export const Placeholder = ({ children }: PropsWithChildren) => (
  <p className="absolute top-4 left-4 p-0.5 opacity-50">{children}</p>
);

const DefaultPlaceholder = () => (
  <Placeholder>
    <FormattedMessage {...defaultPlaceholder} />
  </Placeholder>
);
